import { Menu, Transition, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import clsx from 'clsx';
import {
  Fragment,
  useState,
  type MutableRefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Duration } from 'date-fns';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import DateRangePopup from '~/shared/components/DateRangePopup';
import CalendarIcon from '~/shared/assets/icons/calendar.svg';
import { getDateFilterDates } from '~/shared/utils/getDateFilterDates';
import Typography from '~/shared/components/Typography';

import { formatDate } from '../utils/formatNumber';

import { CUSTOM_DATES_TITLE } from './dateFilterMenuItems';

const transitionProps = {
  enter: 'transition ease-out duration-100',
  enterFrom: 'transform opacity-0 scale-95',
  enterTo: 'transform opacity-100 scale-100',
  leave: 'transition ease-in duration-75',
  leaveFrom: 'transform opacity-100 scale-100',
  leaveTo: 'transform opacity-0 scale-95',
};

function ChartFilterDropdown<
  T extends { title: string; duration?: { start: Duration; end?: Duration } },
>({
  items,
  onSelect,
  selectedItem,
  align = 'top-right',
  fullWidth = false,
  portal = false,
  delegateCustomDateCalendar = false,
  onDelegatedCustomDateOpen,
  menuButtonRef,
}: {
  onSelect: (selection: T) => void;
  items?: T[];
  selectedItem: T;
  align?: 'top-right' | 'top-left';
  fullWidth?: boolean;
  portal?: boolean;
  delegateCustomDateCalendar?: boolean;
  onDelegatedCustomDateOpen?: () => void;
  menuButtonRef?: MutableRefObject<HTMLButtonElement | null>;
}) {
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const isCustomDateSelected = selectedItem.title?.includes(CUSTOM_DATES_TITLE);
  const [startDate, endDate] = getDateFilterDates(selectedItem?.duration, selectedItem.title);

  const handleCustomDateSelect = (startDate: Date, endDate: Date) => {
    const start = startDate.toISOString();
    const end = endDate.toISOString();
    const customItem = {
      title: `${CUSTOM_DATES_TITLE}|${start}|${end}`,
    };

    onSelect(customItem as T);
    setIsCustomDateOpen(false);
  };

  const menuItems = (
    <Transition as={Fragment} {...transitionProps}>
      <MenuItems
        anchor={
          portal
            ? { to: 'bottom start', gap: 10 }
            : undefined
        }
        className={clsx(
          portal
            ? 'rounded-lg bg-white z-[99999]'
            : 'absolute z-60 right-0 top-[100%] rounded-lg mt-2.5 origin-top-right bg-white',
          portal && fullWidth ? 'w-[var(--button-width)]' : fullWidth ? 'w-full' : 'w-60 2xl:w-[290px]',
          'shadow-filter-dropdown border border-primary-70 focus:outline-none side-panel-scrollbar',
          align === 'top-right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left'
        )}
      >
        <div className="p3">
          {items?.map((item) => (
            <MenuItem
              as={'button'}
              key={item.title}
              className={clsx(
                'text-sm leading-6 w-full h-12 2xl:h-14 flex items-center justify-between px-5 py-[18px] border-gray-200 last:rounded-b-lg cursor-pointer hover:bg-primary-20 group',
                items?.[0].title === item.title && 'rounded-t-lg',
                (item.title === selectedItem?.title ||
                  (item.title === CUSTOM_DATES_TITLE && isCustomDateSelected)) &&
                'bg-primary-20 border border-primary-500',
                !(
                  item.title === selectedItem?.title ||
                  (item.title === CUSTOM_DATES_TITLE && isCustomDateSelected)
                ) &&
                item.title !== items?.[items.length - 1]?.title &&
                'border-b border-gray-200'
              )}
              onClick={() => {
                if (item.title === CUSTOM_DATES_TITLE) {
                  if (delegateCustomDateCalendar) {
                    onDelegatedCustomDateOpen?.();
                  } else {
                    setIsCustomDateOpen(true);
                  }
                } else {
                  onSelect?.(item);
                }
              }}
            >
              <Typography color="text-primary-900" variant="p3-reg">
                {item.title}
              </Typography>
              {item.title === 'Custom dates' && (
                <CalendarIcon
                  width="24px"
                  className={clsx(
                    item.title === selectedItem?.title ? 'stroke-[#2D2D32]' : 'stroke-[#2D2D32]',
                    'size-6 group-hover:stroke-[#2D2D32] transition-colors duration-200'
                  )}
                />
              )}
            </MenuItem>
          ))}
        </div>
      </MenuItems>
    </Transition>
  );

  const customDatePopup = (
    <div
      className={clsx(
        portal
          ? 'fixed z-[99999] w-fit max-w-[calc(100vw-16px)] rounded-md shadow-datepicker border border-primary-70 sm:px-0'
          : 'absolute z-[60] top-[100%] mt-1 rounded-md w-fit shadow-datepicker border border-primary-70 sm:px-0',
        !portal && (align === 'top-right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left')
      )}
      style={
        portal
          ? 
            { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', maxHeight: 'calc(100vh - 16px)', overflowY: 'auto' }
          : undefined
      }
    >
      <DateRangePopup
        startDateStr={startDate}
        endDateStr={endDate}
        onSubmit={handleCustomDateSelect}
        onClose={() => setIsCustomDateOpen(false)}
      />
    </div>
  );

  return (
    <div className={clsx('relative', fullWidth && 'w-full')}>
      <Menu as="div" className={clsx('relative flex', fullWidth && 'w-full')}>
        {({ open }) => (
          <>
            <MenuButton
              ref={(el) => {
                if (menuButtonRef) menuButtonRef.current = el;
              }}
              className={clsx(
                'bg-white text-primary-900 border border-gray-300 flex justify-between rounded-[8px] px-2 py-3 h-9 2xl:h-11 cursor-pointer',
                fullWidth ? 'w-full' : 'w-60 2xl:w-[290px] 2xl:min-w-[290px]',
                open
                  ? 'bg-primary-20  border-primary-200'
                  : 'hover:border-primary-300  active:border-primary-100'
              )}
              onClick={() => {
                if (isCustomDateOpen) {
                  setIsCustomDateOpen(false);
                }
              }}
            >
              <div className="flex items-center justify-between w-full">
                <Typography variant="p3-reg" color="text-gray-700">
                  {isCustomDateSelected
                    ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                    : selectedItem?.title}
                </Typography>

                <ChevronDownIcon
                  className={clsx(
                    'w-[14px] h-[14px] text-primary-900 stroke-3 cursor-pointer',
                    open ? 'rotate-180' : ''
                  )}
                />
              </div>
            </MenuButton>

            {portal && typeof document !== 'undefined'
              ? createPortal(menuItems, document.body)
              : menuItems}
          </>
        )}
      </Menu>

      {isCustomDateOpen && (
        portal && typeof document !== 'undefined'
          ? createPortal(customDatePopup, document.body)
          : customDatePopup
      )}
    </div>
  );
}

export default ChartFilterDropdown;
